import { useState } from 'react'
import { useStatusOverlay } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { useUser } from '@fs/zion-user'
import { trackEvent } from '@fs/zion-analytics'
import { setMemoryCache, uploadPhoto } from '../../services/MemoriesAPI/memoriesAPI'
import { useMemoryBook } from '../../providers/MemoryBookProvider'

const useImageHandlers = ({
  memoryId,
  setSavedImage,
  setSelectedImage,
  updatePageContentProps: { pageNumber, positionIndex, caption, title },
  cropArea,
  customLayoutElementId,
  onUpdateCustomLayoutElementData,
}) => {
  const statusOverlay = useStatusOverlay()
  const [t] = useTranslation()
  const { personId: pid } = useUser()
  const [artifactId, setArtifactId] = useState(memoryId || null)
  const { updatePageContent, setMemoryViewerMemoryId } = useMemoryBook()

  // If this is a custom layout element, use the custom update function
  const isCustomLayoutElement = Boolean(customLayoutElementId && onUpdateCustomLayoutElementData)

  const showError = (messageKey, defaultMessage) => {
    statusOverlay({
      message: t(messageKey, defaultMessage),
      type: 'error',
    })
  }

  const updateImage = (artifact) => {
    setSelectedImage(artifact)
    setArtifactId(artifact?.id)
  }

  const onUpload = async (file) => {
    if (!file || !pid) return

    try {
      const { artifact, success } = await uploadPhoto({ file, pid, name: file.name })
      if (!success) throw new Error()

      updateImage(artifact)
      trackEvent({
        event_name: 'memories_upload_photo',
      })
    } catch (error) {
      const messageKey =
        error?.response?.status === 413
          ? 'ImagePickerOverlay.onUpload.failedLargeFile'
          : 'ImagePickerOverlay.onUpload.failedGeneral'
      const defaultMessage =
        error?.response?.status === 413
          ? 'Image uploads cannot be larger than 15MB.'
          : 'An error occurred while uploading the file.'
      showError(messageKey, defaultMessage)
    }
  }

  const onRemove = () => {
    if (!pid) {
      showError('ImagePickerOverlay.onRemove.failed', 'An error occurred while removing the image.')
      return
    }
    setSavedImage(null)
    setMemoryViewerMemoryId(null)
  }

  const onSave = async (image) => {
    if (!image || !pid) return

    try {
      const memoryToUpdateId = artifactId || memoryId

      if (!memoryToUpdateId) {
        showError('ImagePickerOverlay.onSave.failed', 'An error occurred while saving the image.')
        return
      }

      setMemoryViewerMemoryId(memoryToUpdateId)

      setSavedImage(image)
      setMemoryCache(memoryToUpdateId, image)

      const updates = { memoryId: memoryToUpdateId }
      if (cropArea !== undefined) {
        updates.cropArea = cropArea
      }

      // For custom layout elements, use the custom update function
      if (isCustomLayoutElement) {
        // Add caption and title to updates for custom layout elements
        if (caption !== undefined) {
          updates.caption = caption
        }
        if (title !== undefined) {
          updates.title = title
        }
        // Remove placeholder flag when saving
        updates.isPlaceholder = undefined
        await onUpdateCustomLayoutElementData(updates)
      } else {
        // For regular page content, use the standard update function
        await updatePageContent(pageNumber, positionIndex, updates)
      }

      trackEvent({
        event_name: 'memories_save_photo',
      })
    } catch (error) {
      console.error('Error during save:', error)
      showError('ImagePickerOverlay.onSave.failed', 'An error occurred while saving the image.')
    }
  }

  return { onUpload, onRemove, onSave, updateImage }
}

export default useImageHandlers
